import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { WebsiteSourcesAPI } from "../api/website-sources";
import type { WebsiteSource, WebsiteSourceInput } from "../types";
import { documentKeys } from "./use-documents";

export const websiteSourceKeys = {
  all: ["website-sources"] as const,
  list: (clientId: string) => [...websiteSourceKeys.all, clientId] as const,
};

const isBusy = (source: WebsiteSource) =>
  source.status === "pending" || source.status === "indexing";

export function useWebsiteSources() {
  const clientId = useCurrentClient()?.client?.id || "";

  return useQuery<WebsiteSource[]>({
    queryKey: websiteSourceKeys.list(clientId),
    queryFn: () => WebsiteSourcesAPI.list(clientId),
    enabled: !!clientId,
    // A crawl runs in the background: follow it while one is under way.
    refetchInterval: (query) => (query.state.data?.some(isBusy) ? 5000 : false),
  });
}

/** Every change to a source changes the documents it holds. */
const useInvalidate = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: websiteSourceKeys.all });
    queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
  };
};

export function useCreateWebsiteSource() {
  const clientId = useCurrentClient()?.client?.id || "";
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: WebsiteSourceInput) =>
      WebsiteSourcesAPI.create(clientId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateWebsiteSource() {
  const clientId = useCurrentClient()?.client?.id || "";
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: Partial<WebsiteSourceInput>;
    }) => WebsiteSourcesAPI.update(clientId, id, input),
    onSuccess: invalidate,
  });
}

export function useRefreshWebsiteSource() {
  const clientId = useCurrentClient()?.client?.id || "";
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => WebsiteSourcesAPI.refresh(clientId, id),
    onSuccess: invalidate,
  });
}

export function useDeleteWebsiteSource() {
  const clientId = useCurrentClient()?.client?.id || "";
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => WebsiteSourcesAPI.remove(clientId, id),
    onSuccess: invalidate,
  });
}
